import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DialogTimePicker } from '../../../../../../../../components/dialog-time-picker';
import { SelectOptionType } from '../../../../../../../../components/dialog/helper';
import { Header } from '../../../../../../../../components/header';
import { IconEdit } from '../../../../../../../../components/icons';
import { InputSliderDialog } from '../../../../../../../../components/input-slider-dialog';
import { Input } from '../../../../../../../../components/input/input';
import { InputSelect } from '../../../../../../../../components/input/select';
import { SourceThresholdInternal } from '../../../../../../../../data-access/gql-types/graphql';
import { useMeterConfigurationContext } from '../../context';
import { useConfigurationValidation } from '../../hooks/use-configuration-validation';
import { useMeterConfigErrorMessage } from '../../hooks/use-error-message';
import { EventingRuleBody, ThresholdCondition } from '../../types';

type ThresholdPartFormProps = {
  sourceThresholdOptions: SelectOptionType<SourceThresholdInternal>[];
  currentRule: EventingRuleBody;
  thresholdUnit: string;
};

export const ThresholdPartForm: React.FC<ThresholdPartFormProps> = ({
  sourceThresholdOptions,
  currentRule,
  thresholdUnit,
}) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);
  const { activeRuleIndex, form } = useMeterConfigurationContext();
  const { validation, roundStepToOne } = useConfigurationValidation(currentRule.source);
  const { getErrorMessage } = useMeterConfigErrorMessage();

  const thresholdConditions: SelectOptionType<ThresholdCondition>[] = [
    { label: t('meterConfiguration.thresholdEvent.lesserThan'), value: ThresholdCondition.LesserThan },
    { label: t('meterConfiguration.thresholdEvent.greaterThan'), value: ThresholdCondition.GreaterThan },
  ];

  return (
    <>
      <Header
        title={`${t('meterConfiguration.thresholdEvent.setThresholds')} (${t('phase')} ${activeRuleIndex + 1})`}
      />
      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.source`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'source', error);

            return (
              <InputSelect
                options={sourceThresholdOptions}
                value={field.value}
                onChange={field.onChange}
                className="input--reverse m-b-32"
                label={t('meterConfiguration.thresholdEvent.source')}
                {...(errorMessage && { errorMessage })}
              />
            );
          }}
        />
        <Controller
          name={`rules.${activeRuleIndex}.deadTimeInMs`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'deadTimeInMs', error);

            return (
              <>
                <Input
                  defaultValue={field.value}
                  value={field.value}
                  required
                  readOnly
                  label={t('meterConfiguration.thresholdEvent.deadTime')}
                  onClickInput={() => setShowTimePicker(true)}
                  endIcon={<IconEdit />}
                  reverse
                  disabled={!currentRule.source}
                  {...(errorMessage && { errorMessage })}
                />
                <DialogTimePicker
                  header={t('meterConfiguration.thresholdEvent.deadTime')}
                  open={showTimePicker}
                  setOpen={setShowTimePicker}
                  onSave={(value) => field.onChange(value)}
                  time={field.value}
                  showSeconds
                  maxHours={1}
                />
              </>
            );
          }}
        />
      </div>

      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.thresholdCondition`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'thresholdCondition', error);

            return (
              <InputSelect
                onChange={field.onChange}
                value={field.value}
                options={thresholdConditions}
                label={t('meterConfiguration.thresholdEvent.thresholdCondition')}
                name={field.name}
                disabled={!currentRule.source}
                className="input--reverse"
                {...(errorMessage && { errorMessage })}
              />
            );
          }}
        />
        {currentRule.thresholdCondition === ThresholdCondition.GreaterThan && (
          <Controller
            name={`rules.${activeRuleIndex}.minValue`}
            control={form.control}
            rules={{ required: tc('isRequired') as string }}
            render={({ field, fieldState: { error } }) => {
              const errorMessage = getErrorMessage(activeRuleIndex, 'minValue', error);

              return (
                <InputSliderDialog
                  value={field.value || 0}
                  handleSaveValue={(val) => field.onChange(val)}
                  unit={thresholdUnit}
                  min={validation?.validation.min}
                  max={validation?.validation.max}
                  step={roundStepToOne(validation?.validation.step)}
                  dialogTitle={t('meterConfiguration.thresholdEvent.threshold')}
                  inputLabel={t('meterConfiguration.thresholdEvent.threshold')}
                  reverse
                  disabled={!currentRule.source}
                  {...(errorMessage && { errorMessage })}
                />
              );
            }}
          />
        )}
        {currentRule.thresholdCondition === ThresholdCondition.LesserThan && (
          <Controller
            name={`rules.${activeRuleIndex}.maxValue`}
            control={form.control}
            rules={{ required: tc('isRequired') as string }}
            render={({ field, fieldState: { error } }) => {
              const errorMessage = getErrorMessage(activeRuleIndex, 'maxValue', error);

              return (
                <InputSliderDialog
                  value={field.value || 0}
                  handleSaveValue={(val) => field.onChange(val)}
                  unit={thresholdUnit}
                  min={validation?.validation.min}
                  max={validation?.validation.max}
                  step={roundStepToOne(validation?.validation.step)}
                  dialogTitle={t('meterConfiguration.thresholdEvent.threshold')}
                  inputLabel={t('meterConfiguration.thresholdEvent.threshold')}
                  reverse
                  disabled={!currentRule.source}
                  {...(errorMessage && { errorMessage })}
                />
              );
            }}
          />
        )}
      </div>
      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.hysteresis`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'hysteresis', error);

            return (
              <InputSliderDialog
                value={field.value}
                handleSaveValue={(val) => field.onChange(val)}
                unit={thresholdUnit}
                min={validation?.hysteresis.min}
                max={validation?.hysteresis.max}
                step={roundStepToOne(validation?.hysteresis.step)}
                dialogTitle={t('meterConfiguration.thresholdEvent.hysteresis')}
                inputLabel={t('meterConfiguration.thresholdEvent.hysteresis')}
                reverse
                disabled={!currentRule.source}
                {...(errorMessage && { errorMessage })}
              />
            );
          }}
        />
      </div>
    </>
  );
};
